import { mutate } from 'swr';
import { api } from './api';
import { dockBoardKey, refreshDockViews } from './swrKeys';
import toast from 'react-hot-toast';

// Minimal interface for optimistic updates
interface Dock {
    id: string;
    status: string;
    updatedAt?: string;
    [key: string]: any;
}

/**
 * Centralized Dock Operations Mutations
 * Ensures deterministic synchronization between Board, Drawer, and Yard Map.
 */
export const dockMutations = {
    /**
     * Updates dock status with optimistic UI updates and guaranteed revalidation.
     */
    async updateDockStatus(dockId: string, status: string, currentDocks: Dock[] = []) {
        const key = dockBoardKey();

        // 1. Optimistic Update (if we have current data)
        const previousData = currentDocks.length > 0 ? { docks: currentDocks } : undefined;

        if (currentDocks.length > 0) {
            const optimisticData = {
                docks: currentDocks.map(d =>
                    d.id === dockId
                        ? { ...d, status: status, updatedAt: new Date().toISOString() }
                        : d
                )
            };
            // Update cache immediately, do NOT revalidate yet
            await mutate(key, optimisticData, false);
        }

        try {
            // 2. Perform API Call
            await api.updateDock(dockId, { status });

            // 3. Deterministic Refresh
            // This re-fetches the board (updating Map/Grid) and the specific Schedule Drawer
            await refreshDockViews(dockId);

            toast.success(`Dock status updated to ${status}`);

        } catch (error: any) {
            console.error('Dock status update failed', error);
            toast.error('Failed to update dock status');

            // 4. Rollback / Revalidate on error
            if (previousData) {
                await mutate(key, previousData, true);
            } else {
                await mutate(key);
            }
            throw error;
        }
    },

    async deleteDock(dockId: string) {
        try {
            await api.deleteDock(dockId);
            // Hard refresh of the board
            await mutate(dockBoardKey());
            toast.success('Dock deleted successfully');
        } catch (error: any) {
            console.error('Dock deletion failed', error);
            toast.error('Failed to delete dock');
            throw error;
        }
    },

    async createDock(warehouseId: string, data: any) {
        try {
            await api.createDock(warehouseId, data);
            await mutate(dockBoardKey());
            toast.success('Dock created successfully');
        } catch (error: any) {
            console.error('Dock creation failed', error);
            toast.error('Failed to create dock');
            throw error;
        }
    },

    async editDock(dockId: string, data: any) {
        try {
            await api.updateDock(dockId, data);
            await mutate(dockBoardKey());
            toast.success('Dock updated successfully');
        } catch (error: any) {
            console.error('Dock edit failed', error);
            toast.error('Failed to update dock');
            throw error;
        }
    },

    // Appointment Actions that affect Dock Status (Start, Complete, Check-in)
    // These are typically handled in the schedule drawer, but we can centralize here if needed or just use refreshDockViews()
    // For now, ScheduleDrawer handles its own logic but uses refreshDockViews
    async updateDockException(dockId: string, data: { note?: string, reason?: string, ownerUserId?: string | null }) {
        try {
            await api.updateDockException(dockId, data);
            await refreshDockViews(dockId);
            toast.success('Exception updated');
        } catch (error: any) {
            console.error('Exception update failed', error);
            toast.error('Failed to update exception');
            throw error;
        }
    },

    async resolveDockException(dockId: string) {
        try {
            await api.resolveDockException(dockId);
            await refreshDockViews(dockId);
            toast.success('Exception resolved');
        } catch (error: any) {
            console.error('Exception resolve failed', error);
            toast.error('Failed to resolve exception');
            throw error;
        }
    },
};
